(() => {
  const root = window.Shopify?.routes?.root || '/';
  const route = (path) => `${root}${path}`.replace(/\/+/g, '/');
  const jsonRoute = (path) => `${path.replace(/\.js$/, '')}.js`;
  const predictiveRequests = new WeakMap();
  let lastCartTrigger = null;

  const setCartCount = (count) => {
    document.querySelectorAll('[data-cart-count]').forEach((badge) => {
      badge.textContent = String(count);
      badge.hidden = count === 0;
    });
  };

  const drawer = () => document.querySelector('[data-cart-drawer]');

  const openCart = () => {
    const element = drawer();
    if (!element) return;
    element.setAttribute('data-open', '');
    element.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-open');
    element.querySelector('[data-cart-close]')?.focus();
  };

  const closeCart = () => {
    const element = drawer();
    if (!element) return;
    element.removeAttribute('data-open');
    element.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cart-open');
    lastCartTrigger?.focus();
  };

  const setCartStatus = (message) => {
    const status = drawer()?.querySelector('[data-commerce-status]');
    if (status) status.textContent = message;
  };

  const refreshCart = async ({ shouldOpen = true } = {}) => {
    let lastError;
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const [sectionResponse, cartResponse] = await Promise.all([
          fetch(`${root}?section_id=cart-drawer`, { cache: 'no-store', headers: { Accept: 'text/html' } }),
          fetch(jsonRoute(route('cart')), { cache: 'no-store', headers: { Accept: 'application/json' } })
        ]);
        if (!sectionResponse.ok || !cartResponse.ok) {
          throw new Error(`Cart refresh failed (${sectionResponse.status}/${cartResponse.status})`);
        }

        const sectionHtml = await sectionResponse.text();
        const cart = await cartResponse.json();
        const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
        const nextSection = parsed.querySelector('#shopify-section-cart-drawer') || parsed.querySelector('[data-cart-drawer]');
        const currentSection = document.querySelector('#shopify-section-cart-drawer') || document.querySelector('[data-cart-drawer]');
        if (!nextSection || !currentSection) throw new Error('Cart drawer section missing');

        currentSection.replaceWith(nextSection);
        setCartCount(cart.item_count);
        if (shouldOpen) openCart();
        return cart;
      } catch (error) {
        lastError = error;
        if (attempt === 0) await new Promise((resolve) => window.setTimeout(resolve, 150));
      }
    }
    throw lastError || new Error('Cart refresh failed');
  };

  const setButtonLoading = (button, loading) => {
    if (!button) return;
    button.toggleAttribute('data-loading', loading);
    button.disabled = loading;
    button.setAttribute('aria-busy', String(loading));
  };

  const syncSearchOverlay = () => {
    const isOpen = Boolean(document.querySelector('[data-predictive-search-form][data-search-open]'));
    const overlay = document.querySelector('[data-search-overlay]');
    if (overlay) overlay.hidden = !isOpen;
    document.body.classList.toggle('search-open', isOpen);
  };

  const openPredictiveSearch = (form) => {
    document.querySelectorAll('[data-predictive-search-form]').forEach((otherForm) => {
      if (otherForm !== form) closePredictiveSearch(otherForm);
    });
    const input = form.querySelector('[data-predictive-search-input]');
    const panel = form.querySelector('[data-predictive-search-results]');
    if (!input || !panel) return;
    form.setAttribute('data-search-open', '');
    panel.hidden = false;
    input.setAttribute('aria-expanded', 'true');
    syncSearchOverlay();
  };

  const closePredictiveSearch = (form) => {
    const input = form.querySelector('[data-predictive-search-input]');
    const panel = form.querySelector('[data-predictive-search-results]');
    if (!input || !panel) return;
    form.removeAttribute('data-search-open');
    panel.hidden = true;
    panel.innerHTML = '';
    input.setAttribute('aria-expanded', 'false');
    syncSearchOverlay();
  };

  const showDefaultSearch = (form) => {
    const panel = form.querySelector('[data-predictive-search-results]');
    const template = form.querySelector('[data-predictive-search-default]');
    if (!panel || !template) return;
    panel.innerHTML = template.innerHTML;
    openPredictiveSearch(form);
  };

  const loadPredictiveSearch = (form, query) => {
    const input = form.querySelector('[data-predictive-search-input]');
    const panel = form.querySelector('[data-predictive-search-results]');
    if (!input || !panel) return;

    const previous = predictiveRequests.get(form);
    if (previous) {
      window.clearTimeout(previous.timer);
      previous.controller?.abort();
    }

    if (query.length < 2) {
      showDefaultSearch(form);
      return;
    }

    const state = { controller: new AbortController() };
    state.timer = window.setTimeout(async () => {
      const url = new URL(route('search/suggest'), window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', '6');
      url.searchParams.set('resources[options][unavailable_products]', 'hide');
      url.searchParams.set('section_id', 'predictive-search');
      input.setAttribute('aria-busy', 'true');

      try {
        const response = await fetch(url, { signal: state.controller.signal, headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('Predictive search failed');
        panel.innerHTML = await response.text();
        openPredictiveSearch(form);
      } catch (error) {
        if (error.name !== 'AbortError') {
          panel.innerHTML = `<p class="predictive-search__empty">${panel.dataset.errorMessage || 'Zoeken is tijdelijk niet beschikbaar.'}</p>`;
          openPredictiveSearch(form);
        }
      } finally {
        input.removeAttribute('aria-busy');
      }
    }, 250);
    predictiveRequests.set(form, state);
  };

  document.addEventListener('input', (event) => {
    const input = event.target.closest('[data-predictive-search-input]');
    if (!input) return;
    const form = input.closest('[data-predictive-search-form]');
    if (form) loadPredictiveSearch(form, input.value.trim());
  });

  document.addEventListener('focusin', (event) => {
    const input = event.target.closest('[data-predictive-search-input]');
    if (!input) return;
    const form = input.closest('[data-predictive-search-form]');
    if (!form) return;
    if (input.value.trim().length < 2) showDefaultSearch(form);
    else loadPredictiveSearch(form, input.value.trim());
  });

  document.addEventListener('click', async (event) => {
    if (event.target.closest('[data-search-overlay]')) {
      document.querySelectorAll('[data-predictive-search-form]').forEach(closePredictiveSearch);
      return;
    }

    const cartOpen = event.target.closest('[data-cart-open]');
    if (cartOpen) {
      event.preventDefault();
      lastCartTrigger = cartOpen;
      openCart();
      try {
        await refreshCart({ shouldOpen: true });
      } catch {
        setCartStatus(drawer()?.dataset.errorMessage || 'De winkelwagen kan niet worden geladen.');
      }
      return;
    }

    if (event.target.closest('[data-cart-close]')) {
      closeCart();
      return;
    }

    const changeButton = event.target.closest('[data-cart-change]');
    if (changeButton) {
      setButtonLoading(changeButton, true);
      setCartStatus('');
      try {
        const response = await fetch(jsonRoute(route('cart/change')), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line: Number(changeButton.dataset.line), quantity: Number(changeButton.dataset.quantity) })
        });
        if (!response.ok) throw new Error('Cart change failed');
        await refreshCart({ shouldOpen: true });
      } catch {
        setButtonLoading(changeButton, false);
        setCartStatus(drawer()?.dataset.errorMessage || 'De winkelwagen kon niet worden bijgewerkt.');
      }
      return;
    }

    document.querySelectorAll('[data-predictive-search-form]').forEach((form) => {
      if (!form.contains(event.target)) closePredictiveSearch(form);
    });
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-quick-add]');
    if (!form) return;
    event.preventDefault();
    const submit = form.querySelector('[type="submit"]');
    const status = form.querySelector('[data-quick-add-status]');
    setButtonLoading(submit, true);
    if (status) status.textContent = '';

    try {
      const response = await fetch(jsonRoute(form.action), {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || 'Toevoegen aan de winkelwagen is mislukt.');
      lastCartTrigger = submit;
      try {
        await refreshCart({ shouldOpen: true });
      } catch {
        // The add request already succeeded. Keep that success visible instead
        // of making the customer retry and risk adding the item twice.
        openCart();
        if (status) status.textContent = 'Toegevoegd aan je winkelwagen.';
      }
    } catch (error) {
      if (status) status.textContent = error.message || form.dataset.errorMessage;
    } finally {
      setButtonLoading(submit, false);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    if (drawer()?.hasAttribute('data-open')) closeCart();
    document.querySelectorAll('[data-predictive-search-form]').forEach(closePredictiveSearch);
  });
})();
